<template>
  <div class="row">
    <spinner-element :loading="loading" />

    <div class="row" v-if="!loading">
      <div class="col-12" id="details">
        <div class="content">
          <div class="table-container status-actions">
            <button class="btn-prev" @click="goBack">
              {{ t('back').toUpperCase() }}
            </button>
            <button class="btn-next" @click="goToHistory">
              {{ t('location-statuses-history').toUpperCase() }}
            </button>
          </div>
          <div class="status-body">
            <div class="location-table-header">
              <h5 class="status-title">{{ locationTitle }}</h5>
            </div>

            <div v-if="error" class="status-line error">
              {{ error }}
            </div>

            <div v-else-if="!latestStatus" class="status-line">
              {{ t('no-data-from-location') }}
            </div>

            <table v-else class="center-aligned table table-bordered">
              <tbody>
                <tr>
                  <td>
                    <table class="center-aligned table table-striped">
                      <thead>
                        <tr>
                          <th class="field-col">Pole</th>
                          <th class="value-col">Wartość</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in toRows(latestStatus)" :key="row.label">
                          <td>{{ row.label }}</td>
                          <td>{{ row.value }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SpinnerElement from "./SpinnerElement.vue";
import LocationStatusesService from "@/services/LocationStatuses";

require("../assets/css/viver-style.css");

export default {
  name: "LocationLatestStatus",
  components: {
    SpinnerElement,
  },
  props: {
    locationId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      latestStatus: null,
      locationTitle: `ID ${this.locationId}`,
      error: "",
    };
  },
  methods: {
    isNotFoundError(error) {
      return String(error ?? "").includes("404");
    },
    async loadLocationTitle() {
      try {
        const locations = await LocationStatusesService.getLocations();
        const location = (locations || []).find((item) => Number(item.id) === Number(this.locationId));
        if (location) {
          this.locationTitle = `${location.name} (${location.shortName}, ${location.roadNumber})`;
        }
      } catch (error) {
        // fallback title with location id
      }
    },
    async loadLatestStatus() {
      this.loading = true;
      this.error = "";
      try {
        await this.loadLocationTitle();
        const status = await LocationStatusesService.getLatestByLocation(this.locationId);
        this.latestStatus = status || null;
      } catch (error) {
        if (this.isNotFoundError(error)) {
          this.latestStatus = null;
        } else {
          this.error = "Nie udało się pobrać statusu lokalizacji.";
        }
      } finally {
        this.loading = false;
      }
    },
    goToHistory() {
      this.$router.push({ name: "LocationStatusesView" });
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.goToHistory();
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleString();
    },
    formatValue(value) {
      return value === null || value === undefined || value === "" ? "-" : value;
    },
    toRows(status) {
      const labelMap = {
        routerIp: "Adres IP routera",
        statusBattery1: "Akumulator 1",
        statusBattery2: "Akumulator 2",
        statusDoor1: "Drzwi 1",
        statusDoor2: "Drzwi 2",
        statusPwr1: "Zasilanie 1",
        statusPwr2: "Zasilanie 2",
        statusFan1: "Wentylator 1",
        statusFan2: "Wentylator 2",
        statusHeater1: "Grzałka 1",
        statusHeater2: "Grzałka 2",
        statusTempIn1: "Temperatura wewnętrzna 1",
        statusTempIn2: "Temperatura wewnętrzna 2",
        statusHumidity1: "Wilgotność 1",
        statusHumidity2: "Wilgotność 2",
        statusUptime: "Czas pracy",
        statusTempOut1: "Temperatura zewnętrzna 1",
        statusTempOut2: "Temperatura zewnętrzna 2",
        statusCamAnpr1: "Kamera ANPR 1",
        statusCamAnpr2: "Kamera ANPR 2",
        statusCamAnpr3: "Kamera ANPR 3",
        statusCamOvr1: "Kamera OVR 1",
        statusCamOvr2: "Kamera OVR 2",
        statusCamOvr3: "Kamera OVR 3",
        statusCamOvr4: "Kamera OVR 4",
        statusClw1: "Czujnik CLW 1",
        statusClw2: "Czujnik CLW 2",
        statusOverhight1: "Czujnik wysokości 1",
        statusOverhight2: "Czujnik wysokości 2",
        statusInternalVoltage: "Napięcie wewnętrzne",
        statusBatteryVoltage: "Napięcie akumulatora",
        statusBatteryCurrent: "Prąd akumulatora",
        statusPwrCurrent: "Prąd zasilania",
        createdAt: "Data zapisu",
      };

      const fields = [
        "routerIp",
        "statusBattery1",
        "statusBattery2",
        "statusDoor1",
        "statusDoor2",
        "statusPwr1",
        "statusPwr2",
        "statusFan1",
        "statusFan2",
        "statusHeater1",
        "statusHeater2",
        "statusTempIn1",
        "statusTempIn2",
        "statusHumidity1",
        "statusHumidity2",
        "statusUptime",
        "statusTempOut1",
        "statusTempOut2",
        "statusCamAnpr1",
        "statusCamAnpr2",
        "statusCamAnpr3",
        "statusCamOvr1",
        "statusCamOvr2",
        "statusCamOvr3",
        "statusCamOvr4",
        "statusClw1",
        "statusClw2",
        "statusOverhight1",
        "statusOverhight2",
        "statusInternalVoltage",
        "statusBatteryVoltage",
        "statusBatteryCurrent",
        "statusPwrCurrent",
        "createdAt",
      ];

      return fields
        .map((field) => ({
          label: labelMap[field] || field,
          value: field === "statusUptime" || field === "createdAt"
            ? this.formatDateTime(status[field])
            : this.formatValue(status[field]),
        }))
        // Pomijamy puste pola — nie pokazujemy wierszy z myślnikiem "-".
        .filter((row) => row.value !== "-");
    },
  },
  async mounted() {
    await this.loadLatestStatus();
  },
};
</script>

<style scoped src="../assets/css/viver-style.css"></style>

<style scoped>
.row {
  margin: 0 !important;
  padding: 0 !important;
}

.row > [class*="col-"] {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

#details {
  padding: 0 !important;
}

.content {
  /* margin-top: 25px; */
  background-color: white;
}

.status-actions {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  min-height: 56px;
  padding: 0 20px;
  border-bottom: 1px solid #ececec;
}

.status-actions button {
  border: 0;
  font-size: 19px;
  font-weight: bold;
  background: none;
  color: #58585a;
  cursor: pointer;
}

.status-actions .btn-prev {
  padding: 8px 8px 8px 45px;
  position: relative;
}

.status-actions .btn-prev:before {
  content: "";
  position: absolute;
  left: 0;
  top: 11px;
  width: 30px;
  height: 20px;
  background: url("../assets/icons/powrot.png") no-repeat left top;
}

.status-actions .btn-prev:hover {
  color: black;
}

.status-actions .btn-prev:hover:before {
  background: url("../assets/icons/powrot_over.png") no-repeat left top;
}

.status-actions .btn-next {
  padding: 8px 40px 8px 8px;
  position: relative;
}

.status-actions .btn-next:before {
  content: "";
  position: absolute;
  right: 0;
  top: 11px;
  width: 30px;
  height: 20px;
  background: url("../assets/icons/dalej.png") no-repeat left top;
}

.status-actions .btn-next:hover {
  color: black;
}

.status-actions .btn-next:hover:before {
  background: url("../assets/icons/dalej_over.png") no-repeat left top;
}

.location-table-header {
  width: 100%;
  padding: 0 8px;
}

.status-body {
  width: min(100%, 980px);
  margin: 0 auto;
  padding: 0 14px 14px;
}

h5.status-title {
  margin: 12px 0 12px;
  font-size: 18px !important;
  font-weight: bold;
}

table {
  width: 100%;
}

.field-col {
  width: 35%;
}

.value-col {
  width: 65%;
}

.status-line {
  padding: 1rem;
  background: #fff;
  border: 1px solid #ececec;
  margin-bottom: 1rem;
}

.status-line.error {
  color: #b00020;
}
</style>
